import React, {useCallback, useState} from 'react';
import AppsUtils from '../../../../utils/AppsUtil';
import DownloadController from '../../../AppPicker/components/Installation/DownloadController';
import URLUtils from '../../../../utils/Url';
import CCDUtil from '../../../../utils/CCDUtil';
import CampaignCSS from './Campaign.scss';
import {
	DOWNLOAD_CONTROLLER_USER_SOURCE,
	LOGGED_OUT_CAMPAIGN_ENV_NAME,
	LOGGED_OUT_CAMPAIGN_WORKFLOWS
} from '../../../../constants/AppPicker/appPickerConstants';
import uuid from 'uuid';
import AppCard from '@ccxi/ccxi-shared-components/src/AppCard';
import AppPickerAnalytics from '../../../../analytics/AppPickerAnalytics';
import Link from '@react/react-spectrum/Link';

const ANALYTICS = {
	PAGE_RENDER : 'page_render',
	CARD_RENDER: 'card_render',
	CARD_CLICK: 'card_click',
	MOBILE_VIEW: 'Mobile View',
	DOWNLOAD_INSTRUCTIONS_VIEW: 'Email Campaign',
	GOTOCCW_CLICK: 'goToCCWLink'
};

// eslint-disable-next-line camelcase
const getViewObj = view => ({view_type : view});

export default ({
	hasThor,
	locale,
	environment,
	intl,
	routeParams,
	isMobileOrTab,
	isDataAvailable,
	videoData,
	...restProps
}) => {
	const [isMobileRenderCalled, setMobileRenderCalled] = useState(false);
	const {productId} = routeParams;
	const sapcode = AppsUtils.getSapCodeFromProductId(productId);
	const {formatMessage} = intl;

	const analyticsData = {
		event: {
			workflow: 'Campaign CTA',
			subcategory: CCDUtil.getCampaignWorkflowSubcategory()
		},
		env: {
			name: LOGGED_OUT_CAMPAIGN_ENV_NAME,
			version: URLUtils.getParam('campaignKey')
		},
		ui: {
			...getViewObj(ANALYTICS.DOWNLOAD_INSTRUCTIONS_VIEW)
		}
	};

	analyticsData.env.version = URLUtils.getParam('exp');

	const handleAnalyticsEvent = useCallback(({eventType, guid, content}) => {
		const analytics = new AppPickerAnalytics(environment);
		analytics.initAnalytics();
		let eventObj = {};
		switch (eventType) {
			case ANALYTICS.PAGE_RENDER:
				eventObj = {
					type: 'render',
					subtype: 'page'
				};
				break;
			case ANALYTICS.CARD_RENDER:
				eventObj = {
					type: 'render',
					subtype: 'card'
				};
				break;
			case ANALYTICS.CARD_CLICK:
				eventObj = {
					type: 'click',
					subtype: 'card'
				};
				break;
			case ANALYTICS.GOTOCCW_CLICK:
				eventObj = {
					type: 'click',
					subtype: 'GoToCCWeb'
				};
				break;
		}
		if (guid) {
			eventObj.guid = guid;
		}
		const event = {
			...analyticsData.event,
			...eventObj
		};
		let uiObj;
		if (isMobileOrTab) {
			uiObj = {
				...getViewObj(ANALYTICS.MOBILE_VIEW)
			};
		}
		const ui = {
			...analyticsData.ui,
			...uiObj
		};
		return analytics.genericAnalyticEvent({event, content, env: analyticsData.env, ui});
	}, [environment, isMobileOrTab, analyticsData]);

	const onCardCTAClick = useCallback((video) => {
		const {videoUrl, name} = video;
		const guid = uuid.v4();
		const url = AppsUtils.openExternalLink(videoUrl, sapcode, guid);
		handleAnalyticsEvent({
			eventType: ANALYTICS.CARD_CLICK,
			content: {
				action: url,
				name,
			},
			guid
		});
	},[handleAnalyticsEvent, sapcode]);

	const RenderVideoCards = useCallback(() => {
		const {tutorialList} = videoData || {};
		const guid = uuid.v4();
		return (
			tutorialList ? tutorialList.map((video) => {
				handleAnalyticsEvent({
					eventType: ANALYTICS.CARD_RENDER,
					content: {
						action: video.videoUrl,
						name: video.name
					},
					guid
				});
				return (
					<AppCard
						key={`app-card-${video.sys_id}`}
						className='all-apps-app-card'
						title={video.headerText}
						bannerSrc={video.thumbnailImage && video.thumbnailImage.file.url}
						onCardSelect={() => onCardCTAClick(video)}
					/>
				);
			}) : null
		);
	},[handleAnalyticsEvent, onCardCTAClick, videoData]);

	if (!isDataAvailable) {
		return null;
	}

	const onGoToCCWLink = () => {
		const ingestPromise = handleAnalyticsEvent({
			eventType: ANALYTICS.GOTOCCW_CLICK,
		});
		if (ingestPromise && ingestPromise.then) {
			ingestPromise.then(() => {
				window.location = (URLUtils.getParam('workflow') === LOGGED_OUT_CAMPAIGN_WORKFLOWS.OPEN_FILE) ? '/' : '/apps';
			});
		}
		setTimeout(() => {
			window.location = (URLUtils.getParam('workflow') === LOGGED_OUT_CAMPAIGN_WORKFLOWS.OPEN_FILE) ? '/' : '/apps';
		}, 2000);
	};

	const RenderCommonHeader = () => {
		const product = AppsUtils.getFFCProductConfigBySapCode(sapcode);
		const icon = AppsUtils.getProductIcon(product);
		const isVideoCardsAvailable = videoData && videoData.tutorialList;
		const updatesCampaign = URLUtils.getParam('id') === '2020-06-cc-release' && sapcode === 'KCCC';
		let titleStringId = 'apps.loggedOutCampaign.mobile.title';
		let descStringId = 'apps.loggedOutCampaign.mobile.desc';
		if (updatesCampaign) {
			titleStringId = `${titleStringId}.15JunCampaign`;
			descStringId = `${descStringId}.15JunCampaign`;
		}
		else if (URLUtils.getParam('workflow') === LOGGED_OUT_CAMPAIGN_WORKFLOWS.OPEN_FILE) {
			titleStringId = `${titleStringId}.openFile`;
			descStringId = `${descStringId}.openFile`;
		}
		else if (CCDUtil.isRouteToPathCampaign()) {
			descStringId = `${descStringId}.routeToCCD`;
		}

		return (
			<div>
				{icon ? (<img alt={product.displayName} src={icon} />) : []}
				<div className={CampaignCSS.mobileTitle}>
					{formatMessage(
						{id: titleStringId},
						{appName : product.displayName}
					)}
				</div>
				<div className={CampaignCSS.mobileDesc}>
					<span>
						{formatMessage(
							{id: descStringId},
							{appName : product.displayName}
						)}
					</span>
					{(updatesCampaign || (URLUtils.getParam('workflow') === LOGGED_OUT_CAMPAIGN_WORKFLOWS.OPEN_FILE)) && <Link onClick={() => {onGoToCCWLink();}} className={CampaignCSS.ccwLink} variant='quiet'>{formatMessage({id: 'apps.loggedOutCampaign.mobile.goToCCW'})}</Link>}
					{isVideoCardsAvailable && <span className={CampaignCSS.subText}>{formatMessage({id: 'apps.loggedOutCampaign.mobile.subText'}, {appName : product.displayName})}</span>}
				</div>
			</div>
		);
	};

	if (isMobileOrTab) {
		// to avoid re-sending analytics event on every re-render
		if (!isMobileRenderCalled) {
			handleAnalyticsEvent({eventType: ANALYTICS.PAGE_RENDER});
			setMobileRenderCalled(true);
		}
		return (
			<div className={CampaignCSS.mobileContent}>
				<RenderCommonHeader/>
				{<RenderVideoCards/>}
			</div>
		);
	}

	const forceKaizenWorkflow = URLUtils.getParam('workflow') === LOGGED_OUT_CAMPAIGN_WORKFLOWS.DOWNLOAD;

	// Remove Get Help Widget from Route to path workflow
	const isGetHelpWidgetEnabled = !(
		URLUtils.getParam('workflow') === LOGGED_OUT_CAMPAIGN_WORKFLOWS.OPEN_FILE
				&& URLUtils.getParam('file')
	)
		&& !CCDUtil.isRouteToPathCampaign()
		&& !CCDUtil.isProductRouteToPathCampaign(sapcode);

	return (
		<DownloadController
			{...restProps}
			source={DOWNLOAD_CONTROLLER_USER_SOURCE.LOGGED_OUT_CAMPAIGN_PAGE}
			productId={routeParams.productId}
			locale={locale}
			environment={environment}
			intl={intl}
			hasThor={forceKaizenWorkflow ? false : hasThor}
			analyticsData={analyticsData}
			openGetHelpWidget={isGetHelpWidgetEnabled}
		/>
	);
};
